import { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "hooks/reduxHooks";
import Styled from "./Billing.style";
import { selectCurrentClinician } from "../../../../../store/ClinicianStore/selectors";
import { enqueueSnackbar } from "notistack";
import { updateLifetimeAccess } from "repositories/coachApi";
import { icons } from "constants/icons";
import { PaywallModal } from "components/PaywallModal";
import { ManagePlanModal } from "components/ManagePlanModal/ManagePlanModal";
import { getPaymentHistory, getBillingPortalUrl } from "repositories/coachApi";
import { getSubscriptionPlan } from "utils/subscription";
import { AuthRepository } from "repositories/AuthRepository";
import { setCurrentClinician } from "store/ClinicianStore/clinicianSlice";

interface InvoiceItem {
  id: string;
  created: number;
  amount: number;
  receiptUrl: string;
  status: "succeeded" | "pending" | "failed";
}

const Billing = () => {
  const dispatch = useAppDispatch();
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [usedCredits, setUsedCredits] = useState(0);
  const [extraCredits, setExtraCredits] = useState(0);
  const [totalCredits, setTotalCredits] = useState(50);
  const [analysesBlocked, setAnalysesBlocked] = useState(0);
  const [estimatedCost, setEstimatedCost] = useState(100);
  const [paymentMethod] = useState("Payment Method");

  const currentClinician = useAppSelector(selectCurrentClinician);

  const [invoices, setInvoices] = useState<InvoiceItem[]>([]);

  // Poll clinician data every 5 seconds to keep billing info up to date
  useEffect(() => {
    let isMounted = true;
    const authRepository = AuthRepository.getInstance();

    const reloadClinician = async () => {
      try {
        const updatedClinician = await authRepository.getMe();
        if (isMounted) {
          dispatch(setCurrentClinician(updatedClinician));
        }
      } catch {
        // Silently ignore polling errors
      }
    };

    // initial fetch
    reloadClinician();
    // poll every 5 seconds
    const intervalId = window.setInterval(reloadClinician, 5000);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, [dispatch]);

  const handleUpgradePlan = () => {
    setShowManageModal(true);
  };

  const handleExportHistory = () => {
    if (!invoices || invoices.length === 0) {
      enqueueSnackbar("No payment history to export", {
        variant: "info",
      });
      return;
    }

    try {
      const csvHeaders = ["Invoice ID", "Date", "Amount (USD)", "Status"];
      const csvRows = invoices.map((invoice) => [
        invoice.id,
        new Date(invoice.created).toISOString(),
        (invoice.amount / 100).toFixed(2),
        invoice.status,
      ]);

      const escapeCsvField = (value: unknown) => {
        const str = String(value ?? "");
        const needsQuotes = /[",\n]/.test(str);
        const escaped = str.replace(/"/g, '""');
        return needsQuotes ? `"${escaped}"` : escaped;
      };

      const csvContent = [csvHeaders, ...csvRows]
        .map((row) => row.map(escapeCsvField).join(","))
        .join("\n");

      const blob = new Blob([csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, "0");
      const dd = String(now.getDate()).padStart(2, "0");
      link.download = `payment_history_${yyyy}-${mm}-${dd}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      enqueueSnackbar("Payment history exported successfully", {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar("Failed to export payment history", {
        variant: "error",
      });
    }
  };

  const getInvoiceIcon = (status: string) => {
    switch (status) {
      case "succeeded":
        return icons.checkCircle;
      case "pending":
        return icons.pending;
      default:
        return icons.checkCircle;
    }
  };

  const handleManagePlanClick = async (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();
    if (!currentClinician) return;
    if (!currentClinician?.canControlLifetimeAccess) {
      handleUpgradePlan();
      return;
    }
    if (currentClinician?.canControlLifetimeAccess) {
      const hasLifetimeAccess = currentClinician?.hasLifetimeAccess;
      if (hasLifetimeAccess) {
        await updateLifetimeAccess(false);
        enqueueSnackbar("Lifetime access disabled", {
          variant: "success",
        });
      } else {
        await updateLifetimeAccess(true);
        enqueueSnackbar("Lifetime access enabled", {
          variant: "success",
        });
      }
      const authRepository = AuthRepository.getInstance();
      const updatedClinician = await authRepository.getMe();
      dispatch(setCurrentClinician(updatedClinician));
    }
  };

  const openBillingPortal = async () => {
    try {
      const response = await getBillingPortalUrl();
      const url =
        response?.data?.data?.url || response?.data?.url || response?.url;
      if (url) {
        window.location.href = url;
      } else {
        enqueueSnackbar("Failed to open billing portal", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar("Failed to open billing portal", { variant: "error" });
    }
  };

  useEffect(() => {
    if (currentClinician) {
      const currentPlan = getSubscriptionPlan(
        Number(currentClinician?.subscription?.plan),
        currentClinician?.hasLifetimeAccess ?? false
      );
      setUsedCredits(
        currentClinician?.subscription?.billingCycleCreditsUsed || 0
      );
      setTotalCredits(currentPlan?.examsAnalyses);
      setExtraCredits(currentClinician?.subscription?.extraCreditsCount || 0);
      setAnalysesBlocked(currentClinician?.athletesBlockedAmount || 0);
      setEstimatedCost(currentPlan?.cost);
    }
    const fetchPaymentHistory = async () => {
      const paymentHistory = await getPaymentHistory();
      const invoices = paymentHistory.data.payments;
      setInvoices(invoices);
    };
    fetchPaymentHistory();
  }, [currentClinician]);

  return (
    <Styled>
      <div className="billing-section">
        {/* Current Plan Section */}
        <div className="plan-title-container">
          <span className="plan-title">Current Plan</span>
          <div className="plan-badges">
            <span className="free-tier-badge">
              {
                getSubscriptionPlan(
                  Number(currentClinician?.subscription?.plan),
                  currentClinician?.hasLifetimeAccess ?? false
                )?.name
              }
            </span>
            {currentClinician?.subscription?.cancelledAt && (
              <span className="unsubscribed-badge">Unsubscribed</span>
            )}
          </div>
        </div>
        <div className="billing-card">
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-icon athletes">
                <img src={icons.directionRun} alt="directionRun" />
              </div>
              <div className="stat-content">
                <div className="stat-label">Exam credits used</div>
                {!currentClinician?.subscription?.subscribed &&
                currentClinician?.subscription?.cancelledAt ? (
                  <div className="stat-value">Not subscribed</div>
                ) : (
                  <div className="stat-value">
                    {usedCredits}/{totalCredits}
                  </div>
                )}
              </div>
              {extraCredits > 0 && (
                <div className="stat-label">
                  Extra credits: <strong>{extraCredits}</strong>
                </div>
              )}
            </div>

            <div className="stat-item">
              <div className="stat-icon analyses">
                <img src={icons.blockIcon} alt="blockIcon" />
              </div>
              <div className="stat-content">
                <div className="stat-label">Analyses blocked</div>
                <div className="stat-value">{analysesBlocked}</div>
              </div>
            </div>

            <div className="stat-item">
              <div className="stat-icon cost">
                <img src={icons.moneyIcon} alt="moneyIcon" />
              </div>
              <div className="stat-content">
                <div className="stat-label">Monthly cost</div>
                <div className="stat-value">${estimatedCost}</div>
              </div>
              <button className="upgrade-link" onClick={handleManagePlanClick}>
                {currentClinician?.canControlLifetimeAccess
                  ? `${
                      currentClinician?.hasLifetimeAccess ? "Disable" : "Enable"
                    } lifetime access`
                  : "Manage plan"}
              </button>
            </div>
          </div>
        </div>

        {/* Payment Method Section */}
        <div className="card-title">Payment Method</div>
        <div className="billing-card border">
          <div className="payment-method-content">
            <div className="payment-info">
              <div className="payment-icon">S</div>
              <span className="payment-text">{paymentMethod}</span>
            </div>
            <span
              onClick={openBillingPortal}
              className="status-badge"
              style={{
                backgroundColor: currentClinician?.subscription
                  ?.hasPaymentMethod
                  ? "#10b981"
                  : "red",
                cursor: "pointer",
              }}
            >
              {"Manage"}
            </span>
          </div>
        </div>

        {/* Next Billing Date Section */}
        <div className="card-title">
          {currentClinician?.subscription?.cancelledAt
            ? "Billing cycle ends"
            : "Next billing date"}
        </div>
        <div className="billing-card border">
          <div className="payment-method-content">
            <div className="payment-info">
              <div className="payment-icon">
                <img src={icons.calendar} alt="calendar" />
              </div>
              <span className="payment-text">
                {currentClinician?.subscription?.cancelledAt
                  ? "Billing cycle ends"
                  : "Next billing date"}
              </span>
            </div>
            <span
              className="status-badge"
              style={{
                backgroundColor: "#374151",
              }}
            >
              {currentClinician?.subscription?.nextBillingDate
                ? new Date(
                    (currentClinician.subscription.nextBillingDate as number) *
                      1000
                  ).toLocaleDateString()
                : "—"}
            </span>
          </div>
        </div>

        {/* Payment History Section */}
        <div className="billing-card">
          <div className="payment-history-header">
            <span className="card-title">Payment History</span>
            <a
              href="#"
              className="export-link"
              onClick={(e) => {
                e.preventDefault();
                handleExportHistory();
              }}
            >
              Export as CSV
            </a>
          </div>

          <div className="invoice-list">
            {invoices.map((invoice) => (
              <div key={invoice.id} className="invoice-item border">
                <div className="invoice-info">
                  <div className={`invoice-icon ${invoice.status}`}>
                    <img
                      src={getInvoiceIcon(invoice.status)}
                      alt={invoice.status}
                    />
                  </div>
                  <div className="invoice-details">
                    <div className="invoice-id">
                      Invoice #{invoice.id.split("_")[1]}
                    </div>
                    <div className="invoice-date">
                      {new Date(invoice.created).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <div className="invoice-right">
                  <span className="invoice-amount">
                    ${invoice.amount / 100}
                  </span>
                  <span className={`invoice-status ${invoice.status}`}>
                    {invoice.status.charAt(0).toUpperCase() +
                      invoice.status.slice(1)}
                  </span>
                  <a
                    href={invoice.receiptUrl}
                    target="_blank"
                    className="download-link"
                  >
                    <img src={icons.download} alt="download" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Manage Plan Modal */}
      <ManagePlanModal
        open={showManageModal}
        onClose={() => setShowManageModal(false)}
        onUpgrade={() => {
          setShowManageModal(false);
          setShowPaywallModal(true);
        }}
      />

      {/* Paywall Modal */}
      <PaywallModal
        open={showPaywallModal}
        onDowngrade={() => {
          setShowPaywallModal(false);
          setShowManageModal(true);
        }}
        onClose={() => setShowPaywallModal(false)}
        athleteAttempts={
          currentClinician?.athletesBlocked as unknown as Array<{
            athleteId: string;
            athleteName: string;
            avatar?: string;
            attempts: number;
          }>
        }
        totalAthletes={analysesBlocked}
      />
    </Styled>
  );
};

export default Billing;
